/**
 * Checklist 753 — Audit Conformitate Ghid OMS Igiena Mâinilor
 * PRD: docs/PRD_Checklist753_Evaluari.md
 * Binary DA/NU/N/A per question, 7 themes, 105 questions. Results saved to assessment_results.
 * 
 * AUDIT CONTEXT SUPPORT (PRD: docs/PRD_Audit_Lifecycle_v2.md Section 7.3):
 * When auditContext prop is provided, filters questions to selectedThemeIds only,
 * saves to assessment_instances instead of assessment_results.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import assessmentInstrumentService from '../../services/assessmentInstrumentService';
import assessmentResultsService from '../../services/assessmentResultsService';
import { assessmentInstanceService } from '../../services/assessmentInstanceService';
import { useNotifications } from '../NotificationSystem';
import './WHOAssessment.css';

const INSTRUMENT_ID = 'CHECKLIST_753_WHO_HAND_HYGIENE';
const TOTAL_QUESTIONS = 105;

/**
 * Map stored scope to this instrument's component ids (T1…T7).
 * Empty or non-matching ids (e.g. hospital audit_themes like theme_igiena_maini) → use full instrument scope.
 */
function resolveScopedThemeIds(allComponents, rawIds) {
  const ids = Array.isArray(rawIds) ? rawIds.filter(Boolean) : [];
  const componentIds = (allComponents || []).map((c) => c.id);
  if (componentIds.length === 0) {
    return [];
  }

  if (ids.length === 0) {
    return [...componentIds];
  }

  const valid = ids.filter((id) => componentIds.includes(id));
  if (valid.length === 0) {
    console.warn(
      '[Checklist753] Instance theme ids do not match instrument components; using full checklist scope.',
      ids,
      'expected one of:',
      componentIds
    );
    return [...componentIds];
  }
  return valid;
}

function scoreTheme(responses, themeId) {
  const themeResponses = responses.filter((r) => r.themeId === themeId && r.response !== 'N/A');
  const applicable = themeResponses.length;
  if (applicable === 0) return null;
  const score = themeResponses.filter((r) => r.response === 'DA').length;
  return {
    themeId,
    score,
    applicable,
    percentage: Math.round((score / applicable) * 100),
  };
}

function scoreTotal(responses) {
  const applicable = responses.filter((r) => r.response !== 'N/A');
  if (applicable.length === 0) return null;
  const score = applicable.filter((r) => r.response === 'DA').length;
  return {
    score,
    applicable: applicable.length,
    totalQuestions: TOTAL_QUESTIONS,
    percentage: Math.round((score / applicable.length) * 100),
  };
}

/** RO copy from Firestore often says “teme”; UI uses “capitole” for WHO sections. */
function localizeChecklistDescriptionRo(text) {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(
      /\b(\d+)\s+întrebări\s+pe\s+(\d+)\s+teme\b/gi,
      '$1 întrebări pe $2 capitole'
    )
    .replace(/\bpe\s+(\d+)\s+teme\b/gi, 'pe $1 capitole');
}

function localizeChecklistDescriptionEn(text) {
  if (!text || typeof text !== 'string') return text;
  return text
    .replace(
      /\b(\d+)\s+questions\s+across\s+(\d+)\s+themes\b/gi,
      '$1 questions across $2 chapters'
    )
    .replace(/\bacross\s+(\d+)\s+themes\b/gi, 'across $1 chapters');
}

const Checklist753Assessment = ({ 
  organizationId, 
  userId, 
  userName, 
  onComplete,
  onBack,
  auditContext = null  // { auditId, instanceId, selectedThemeIds, auditTitle }
}) => {
  const { t, i18n } = useTranslation(['qms', 'common']);
  const { notify } = useNotifications();
  const [instrument, setInstrument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [activeThemeIndex, setActiveThemeIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  /** Canonical row from assessment_instances (selectedThemeIds + answers) */
  const [auditInstanceRow, setAuditInstanceRow] = useState(null);
  /** Wait for Firestore instance before rendering scoped UI (avoids sidebar/questions mismatch). */
  const [auditInstanceReady, setAuditInstanceReady] = useState(
    () => !auditContext?.instanceId
  );

  const useRo = (i18n.language || 'ro').startsWith('ro');
  
  // Audit context mode
  const isAuditMode = !!auditContext;

  // Single fetch: answers + selectedThemeIds from assessment_instances (source of truth)
  useEffect(() => {
    if (!isAuditMode || !auditContext?.instanceId) {
      setAuditInstanceRow(null);
      setAuditInstanceReady(true);
      return;
    }
    let cancelled = false;
    setAuditInstanceReady(false);
    (async () => {
      try {
        const instance = await assessmentInstanceService.getById(auditContext.instanceId);
        if (cancelled) return;
        setAuditInstanceRow(instance || null);
        setActiveThemeIndex(0);
        if (instance?.answers) {
          const converted = {};
          Object.keys(instance.answers).forEach((qId) => {
            converted[qId] = instance.answers[qId].value;
          });
          setAnswers(converted);
        }
      } catch (err) {
        console.error('[Checklist753] Load assessment instance failed:', err);
        if (!cancelled) setAuditInstanceRow(null);
      } finally {
        if (!cancelled) setAuditInstanceReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuditMode, auditContext?.instanceId]);

  useEffect(() => {
    let cancelled = false;
    assessmentInstrumentService
      .getInstrument(INSTRUMENT_ID)
      .then((data) => {
        if (!cancelled) {
          setInstrument(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Checklist753 load:', err);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const rawScopedThemeIds =
    auditInstanceRow?.selectedThemeIds ?? auditContext?.selectedThemeIds;

  const effectiveSelectedThemeIds = React.useMemo(
    () => resolveScopedThemeIds(instrument?.components || [], rawScopedThemeIds),
    [instrument, rawScopedThemeIds]
  );

  const components = React.useMemo(() => {
    const allComponents = instrument?.components || [];
    if (!isAuditMode) return allComponents;
    if (!effectiveSelectedThemeIds.length) return allComponents;
    return allComponents.filter((comp) => effectiveSelectedThemeIds.includes(comp.id));
  }, [instrument, isAuditMode, effectiveSelectedThemeIds]);

  const activeComponentId = components[activeThemeIndex]?.id;

  useEffect(() => {
    if (activeThemeIndex >= components.length) {
      setActiveThemeIndex(0);
    }
  }, [components.length, activeThemeIndex]);

  const handleAnswer = async (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
    
    // If in audit mode, save answer to assessment_instances
    if (isAuditMode && auditContext?.instanceId) {
      try {
        await assessmentInstanceService.saveAnswer(
          auditContext.instanceId,
          questionId,
          value,
          userId
        );
      } catch (err) {
        console.error('[Checklist753] Save answer failed:', err);
      }
    }
  };

  const handleSubmit = async () => {
    if (!organizationId || !userId) {
      notify.error(t('qms:selectOrganization') || 'Select an organization.');
      return;
    }

    setSubmitting(true);
    try {
      if (isAuditMode) {
        // AUDIT MODE: Complete assessment instance with scoped scoring
        const resultSummary = await assessmentInstanceService.complete(
          auditContext.instanceId,
          instrument,
          effectiveSelectedThemeIds
        );
        
        notify.success(t('qms:whoAssessments.assessmentSubmitted') || 'Assessment submitted successfully.');
        
        onComplete?.({
          instrumentType: 'checklist753',
          instrument,
          mode: 'audit',
          auditId: auditContext.auditId,
          instanceId: auditContext.instanceId,
          resultSummary,
          answers
        });
      } else {
        // STANDALONE MODE: Save to assessment_results (existing behavior)
        const components = instrument?.components || [];
        const responses = [];
        components.forEach((comp) => {
          (comp.questions || []).forEach((q) => {
            const val = answers[q.id];
            responses.push({
              questionId: q.id,
              themeId: comp.id,
              response: val === 'DA' ? 'DA' : val === 'NU' ? 'NU' : 'N/A',
            });
          });
        });

        const totalResult = scoreTotal(responses);
        if (!totalResult) {
          notify.error(t('qms:whoAssessments.allNaError') || 'At least one question must be applicable (not all N/A).');
          return;
        }

        const themeScores = [];
        components.forEach((comp) => {
          const ts = scoreTheme(responses, comp.id);
          themeScores.push({
            themeId: comp.id,
            themeCode: comp.code,
            nameRo: comp.nameRo || comp.name,
            score: ts?.score ?? 0,
            applicable: ts?.applicable ?? 0,
            percentage: ts?.percentage ?? 0,
          });
        });

        await assessmentResultsService.submitChecklist753Result(
          organizationId,
          {
            totalScore: totalResult.score,
            totalApplicable: totalResult.applicable,
            totalPercentage: totalResult.percentage,
            themeScores,
            responses,
          },
          userId,
          userName
        );
        notify.success(t('qms:whoAssessments.assessmentSubmitted') || 'Assessment submitted successfully.');

        const excludedNa = TOTAL_QUESTIONS - totalResult.applicable;
        onComplete?.({
          instrumentType: 'checklist753',
          instrument,
          mode: 'standalone',
          totalScore: totalResult.score,
          totalApplicable: totalResult.applicable,
          totalPercentage: totalResult.percentage,
          totalQuestions: TOTAL_QUESTIONS,
          excludedNa,
          themeScores,
          responses,
          answers,
        });
      }
    } catch (err) {
      notify.error(err.message || t('common:saveFailed') || 'Save failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const activeTheme = components[activeThemeIndex];

  const questionsByChapter = React.useMemo(() => {
    const map = {};
    (activeTheme?.questions || []).forEach((q) => {
      const ch = q.chapterCode || 'other';
      if (!map[ch]) map[ch] = [];
      map[ch].push(q);
    });
    return map;
  }, [activeTheme]);

  const chapterOrder = React.useMemo(() => {
    const seen = [];
    (activeTheme?.questions || []).forEach((q) => {
      const ch = q.chapterCode || 'other';
      if (!seen.includes(ch)) seen.push(ch);
    });
    return seen;
  }, [activeTheme]);
  
  // Calculate total questions in scope
  const totalQuestionsInScope = React.useMemo(() => {
    if (isAuditMode) {
      return components.reduce((sum, comp) => sum + (comp.questions?.length || 0), 0);
    }
    return TOTAL_QUESTIONS;
  }, [components, isAuditMode]);

  if (loading || (isAuditMode && auditContext?.instanceId && !auditInstanceReady)) {
    return (
      <div className="who-assessment loading">
        <p>{t('qms:loading') || 'Loading…'}</p>
      </div>
    );
  }

  if (!instrument) {
    return (
      <div className="who-assessment error">
        <p>{t('qms:whoAssessments.instrumentNotFound') || 'Assessment instrument not found. Please contact your administrator.'}</p>
      </div>
    );
  }

  const totalAnswered = Object.keys(answers).filter(
    (k) => answers[k] !== undefined && answers[k] !== null && answers[k] !== ''
  ).length;
  const canSubmit = totalAnswered >= totalQuestionsInScope;

  const descriptionRoRaw = instrument.descriptionRo || instrument.description;
  const descriptionEnRaw = instrument.description || instrument.descriptionRo;
  const headerSubtitle = useRo
    ? localizeChecklistDescriptionRo(descriptionRoRaw)
    : localizeChecklistDescriptionEn(descriptionEnRaw);

  return (
    <div className="who-assessment who-checklist">
      {isAuditMode && (
        <div className="who-assessment-audit-banner">
          <div className="audit-banner-inner">
            {typeof onBack === 'function' && (
              <button
                type="button"
                className="who-assessment-back-btn"
                onClick={() => onBack()}
              >
                ← {t('qms:whoAssessments.backToAudit') || 'Back to audit'}
              </button>
            )}
            <div className="audit-banner-content">
              {auditContext?.auditTitle && (
                <>
                  <span className="audit-banner-label">{t('qms:audit') || 'Audit'}:</span>
                  <span className="audit-banner-title">{auditContext.auditTitle}</span>
                  <span className="audit-banner-separator">|</span>
                </>
              )}
              <span className="audit-banner-scope">
                {t('qms:scope') || 'Scope'}:{' '}
                {t('qms:checklistScopeCount', {
                  count: effectiveSelectedThemeIds.length,
                })}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="who-assessment-header-row">
        <div className="who-assessment-header-titles">
          <h2>{useRo ? (instrument.nameRo || instrument.name) : (instrument.name || instrument.nameRo)}</h2>
          {headerSubtitle && (
            <p className="who-assessment-header-subtitle">
              {headerSubtitle}
            </p>
          )}
        </div>
        <div className="who-assessment-header-meta">
          <span className="who-assessment-pill" aria-label={t('qms:whoAssessments.questionsAnswered')}>
            {totalAnswered} / {totalQuestionsInScope} {useRo ? 'răspunse' : 'answered'}
          </span>
          <div className="who-assessment-submit-wrap">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={submitting || !canSubmit}
            >
              {submitting
                ? (t('qms:saving') || 'Saving…')
                : (t('qms:whoAssessments.submitAssessment') || 'Submit Assessment')}
            </button>
          </div>
        </div>
      </div>

      <div className="who-assessment-with-sidebar">
        <nav
          className="who-assessment-sidebar"
          aria-label={
            t('qms:whoAssessments.checklistChaptersNav') ||
            t('qms:whoAssessments.themeBreakdown') ||
            'Chapters'
          }
        >
          <div className="who-assessment-sidebar-nav">
            {(instrument?.components || []).map((comp, idx) => {
              const isInScope = !isAuditMode || effectiveSelectedThemeIds.includes(comp.id);
              const themeQuestions = comp.questions || [];
              const themeAnswered = themeQuestions.filter(
                (q) => answers[q.id] !== undefined && answers[q.id] !== null && answers[q.id] !== ''
              ).length;
              const isCompleted = themeQuestions.length > 0 && themeAnswered === themeQuestions.length;
              const scopedIdx = components.findIndex((c) => c.id === comp.id);
              const isActive =
                isInScope && !!activeComponentId && comp.id === activeComponentId;

              return (
                <button
                  key={comp.id}
                  type="button"
                  className={`who-assessment-sidebar-item ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${!isInScope ? 'out-of-scope' : ''}`}
                  onClick={() =>
                    isInScope && scopedIdx >= 0 && setActiveThemeIndex(scopedIdx)
                  }
                  disabled={!isInScope}
                  title={!isInScope ? (t('qms:outOfScope') || 'Out of scope') : ''}
                >
                  <span className="who-assessment-sidebar-item-name">
                    {comp.code}. {useRo ? (comp.nameRo || comp.name) : (comp.name || comp.nameRo)}
                    {!isInScope && <span className="scope-badge">{t('qms:outOfScope') || 'Out of scope'}</span>}
                  </span>
                  {isInScope && (
                    <span className="who-assessment-sidebar-item-count">
                      {themeAnswered} / {themeQuestions.length}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </nav>

        <main className="who-assessment-main">
          {activeTheme && (
            <>
              {chapterOrder.map((chapterCode) => (
                <React.Fragment key={chapterCode}>
                  <div className="who-assessment-chapter-divider">
                    <span className="who-assessment-chapter-badge">Cap. {chapterCode}</span>
                    <span className="who-assessment-chapter-line" />
                  </div>
                  {(questionsByChapter[chapterCode] || []).map((q) => (
                    <div key={q.id} className="who-assessment-question-card">
                      <div className="who-assessment-question-inner">
                        <span className="who-assessment-question-code">{q.code}</span>
                        <p className="who-assessment-question-text">
                          {useRo ? (q.textRo || q.text) : (q.text || q.textRo)}
                        </p>
                        <div className="who-assessment-question-toggles" role="group" aria-label={q.id}>
                          <button
                            type="button"
                            className={`who-assessment-toggle-btn ${answers[q.id] === 'DA' ? 'selected-da' : ''}`}
                            onClick={() => handleAnswer(q.id, 'DA')}
                          >
                            DA
                          </button>
                          <button
                            type="button"
                            className={`who-assessment-toggle-btn ${answers[q.id] === 'NU' ? 'selected-nu' : ''}`}
                            onClick={() => handleAnswer(q.id, 'NU')}
                          >
                            NU
                          </button>
                          <button
                            type="button"
                            className={`who-assessment-toggle-btn ${answers[q.id] === 'N/A' ? 'selected-na' : ''}`}
                            onClick={() => handleAnswer(q.id, 'N/A')}
                          >
                            N/A
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default Checklist753Assessment;
